import { NotificationItem, User, UserNotification } from "01Data/Account";
import { AppContext } from "01Data/AppData";
import PageTemplate from "04UserPages/00PageTemplate/PageTemplate";
import {
  BreadCrumbItem,
  BreadCrumbList,
} from "04UserPages/99Parts/BreadCrumbList";
import { useModel } from "99Tools/useModel";
import { LoadingButton } from "@mui/lab";
import { Backdrop, Box, CircularProgress, styled } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { LIST_HEADER_COLOR } from "Settings";
import { useContext, useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";

const ColorHeader = styled(TableCell)(({ theme }) => ({
  backgroundColor: LIST_HEADER_COLOR,
  color: theme.palette.common.white,
}));

const ButtonChecked = styled(LoadingButton)(({ theme }) => ({
  color: theme.palette.common.white,
  border: "solid 1px",
  backgroundColor: "#009944",
  "&:hover": {
    color: theme.palette.common.white,
    border: "solid 1px",
    backgroundColor: "#69BD83",
  },
}));

const ButtonUnChecked = styled(LoadingButton)(({ theme }) => ({
  border: "solid 1px",
  borderColor: "#009944",
  "&:hover": {
    border: "solid 1px",
    borderColor: "#69BD83",
  },
}));

const MobileLabel = styled("span")(() => ({
  lineHeight: 1,
  color: "gray",
  textAlign: "left",
  whiteSpace: "nowrap",
}));
const MobileItem = styled("div")(() => ({
  margin: 0,
  padding: 0,
  lineHeight: 1,
  textAlign: "left",
  overflowWrap: "anywhere",
  wordBreak: "break-all",
  // overflow: "hidden",
  // whiteSpace: "nowrap",
  // textOverflow: "ellipsis",
}));

/**
 * カラム設定
 */
const columns = [
  {
    id: "deviceName",
    label: "端末名称",
    width: null,
  },
  {
    id: "buildingName",
    label: "建物名",
  },
  {
    id: "location",
    label: "設置場所",
  },
  {
    id: "andonDisplay",
    label: "ステータス表示",
  },
  {
    id: "mailNotice",
    label: "メール通知",
  },
];

const mobileColumns = [
  {
    id: "deviceName",
    label: "端末",
  },
  {
    id: "andonDisplay",
    label: "ステータス表示",
  },
  {
    id: "mailNotice",
    label: "メール通知",
  },
];

type DeviceBodyProps = {
  isSmartphne: boolean;
};

const DeviceBody: React.FC<DeviceBodyProps> = ({
  isSmartphne: isSmartphone,
}) => {
  const d = useContext(AppContext).dataUser;
  const p = d.pageNotification;
  const [loading, setLoading] = useState(new Map<string, boolean>());

  const selected = d.processNotification.listData.find(
    (userNotification: UserNotification) =>
      userNotification.user.UserId === d.processNotification.selectedUser.UserId
  );
  if (!selected) {
    return <TableBody></TableBody>;
  }

  const handleShowClick = (item: NotificationItem, i: number) => {
    item.flags.DisplayFlg = item.flags.DisplayFlg === "true" ? "false" : "true";
    loading.set(`show_${i}`, true);
    setLoading(new Map<string, boolean>(loading));
    item
      .EditUserNotification(selected.user)
      .then(() => {
        p.changed();
      })
      .finally(() => {
        loading.set(`show_${i}`, false);
        setLoading(new Map<string, boolean>(loading));
      });
  };

  const handleMailClick = (item: NotificationItem, i: number) => {
    item.flags.MailFlg = item.flags.MailFlg === "true" ? "false" : "true";
    loading.set(`mail_${i}`, true);
    setLoading(new Map<string, boolean>(loading));
    item
      .EditUserNotification(selected.user)
      .then(() => {
        p.changed();
      })
      .finally(() => {
        loading.set(`mail_${i}`, false);
        setLoading(new Map<string, boolean>(loading));
      });
  };

  const rows = selected.items
    .filter((row: NotificationItem) => row.deviceKind !== "pico3")
    .sort((a, b) => {
      if (a.buildingName === b.buildingName) {
        return a.deviceName.localeCompare(b.deviceName);
      } else {
        return a.buildingName.localeCompare(b.buildingName);
      }
    })
    .slice(
      p.list.page * p.list.rowsPerPage,
      p.list.page * p.list.rowsPerPage + p.list.rowsPerPage
    )
    .map((row: NotificationItem, i: number) => {
      return (
        <TableRow
          hover
          role="checkbox"
          tabIndex={-1}
          key={row.flags.SensorId + row.flags.BuildingId}
        >
          {isSmartphone ? (
            <TableCell
              key={columns[0].id + i}
              align="center"
              sx={{ fontSize: "12px", lineHeight: 0.6, padding: "8px" }}
            >
              <Box sx={{ maxWidth: "150px" }}>
                <MobileItem>
                  <MobileLabel>名称：</MobileLabel>
                  {row.deviceName}
                </MobileItem>
                <br />
                <MobileItem>
                  <MobileLabel>建物：</MobileLabel>
                  {row.buildingName}
                </MobileItem>
                {row.locationName ? (
                  <>
                    <br />
                    <MobileItem>
                      <MobileLabel>場所：</MobileLabel>
                      {row.locationName}
                    </MobileItem>
                  </>
                ) : null}
              </Box>
            </TableCell>
          ) : (
            <>
              <TableCell key={columns[0].id + i} align="center">
                {row.deviceName}
              </TableCell>
              <TableCell key={columns[1].id + i} align="center">
                {row.buildingName}
              </TableCell>
              <TableCell key={columns[2].id + i} align="center">
                {row.locationName}
              </TableCell>
            </>
          )}
          <TableCell key={columns[isSmartphone ? 1 : 3].id + i} align="center">
            {row.flags.DisplayFlg === "true" ? (
              <ButtonChecked
                size={isSmartphone ? "small" : "medium"}
                onClick={() => handleShowClick(row, i)}
                loading={loading.get(`show_${i}`)}
                sx={isSmartphone ? { minWidth: "48px" } : {}}
              >
                {"✔"}
              </ButtonChecked>
            ) : (
              <ButtonUnChecked
                size={isSmartphone ? "small" : "medium"}
                onClick={() => handleShowClick(row, i)}
                loading={loading.get(`show_${i}`)}
                sx={isSmartphone ? { minWidth: "48px" } : {}}
              >
                {"　"}
              </ButtonUnChecked>
            )}
          </TableCell>
          <TableCell key={columns[isSmartphone ? 2 : 4].id + i} align="center">
            {row.flags.MailFlg === "true" ? (
              <ButtonChecked
                size={isSmartphone ? "small" : "medium"}
                onClick={() => handleMailClick(row, i)}
                loading={loading.get(`mail_${i}`)}
                sx={isSmartphone ? { minWidth: "48px" } : {}}
              >
                {"✔"}
              </ButtonChecked>
            ) : (
              <ButtonUnChecked
                size={isSmartphone ? "small" : "medium"}
                onClick={() => handleMailClick(row, i)}
                loading={loading.get(`mail_${i}`)}
                sx={isSmartphone ? { minWidth: "48px" } : {}}
              >
                {"　"}
              </ButtonUnChecked>
            )}
          </TableCell>
        </TableRow>
      );
    });

  return <TableBody>{rows}</TableBody>;
};

const DeviceTable = () => {
  const d = useContext(AppContext).dataUser;
  const p = d.pageNotification;

  const isSmartphone = useMediaQuery({
    query: "(max-width: 600px)",
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    p.list.page = newPage;
    p.changed();
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    p.list.rowsPerPage = +event.target.value;
    p.list.page = 0;
    p.changed();
  };

  const header = (isSmartphone ? mobileColumns : columns).map((column) => (
    <ColorHeader
      key={column.id}
      align="center"
      sx={isSmartphone ? { fontSize: "11px" } : {}}
      size={isSmartphone ? "small" : "medium"}
    >
      {column.label}
    </ColorHeader>
  ));

  const selectedItems =
    d.processNotification.listData
      .find(
        (userNotification: UserNotification) =>
          userNotification.user.UserId ===
          d.processNotification.selectedUser.UserId
      )
      ?.items.filter((item: NotificationItem) => item.deviceKind !== "pico3") ??
    new UserNotification().items;

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer sx={{ minHeight: "60vh" }}>
        <Table stickyHeader aria-label="sticky table" size="small">
          <TableHead style={{ whiteSpace: "nowrap" }}>
            <TableRow>{header}</TableRow>
          </TableHead>
          <DeviceBody isSmartphne={isSmartphone} />
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={selectedItems.length}
        rowsPerPage={p.list.rowsPerPage}
        page={p.list.page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

const Contents = () => {
  const d = useContext(AppContext).dataUser;
  const [loading, setLoading] = useState(false);
  const p = d.pageNotification;
  useModel(p);
  const types = ["administrtaor", "sub_admin", "manager", "user"];

  useEffect(() => {
    setLoading(true);
    d.processNotification.Clear();
    d.processNotification
      .Load()
      .then(() => {
        d.processNotification.users = d.processNotification.users.sort(
          (a, b) => {
            if (a.AccountType === b.AccountType) {
              if (a.family_name === b.family_name) {
                return a.given_name.localeCompare(b.given_name);
              } else {
                return a.family_name.localeCompare(b.family_name);
              }
            } else {
              return types.indexOf(a.AccountType) > types.indexOf(b.AccountType)
                ? 1
                : -1;
            }
          }
        );
        if (d.processNotification.users.length > 0) {
          d.processNotification.selectedUser = d.processNotification.users[0];
        }
        p.changed();
      })
      .finally(() => {
        setLoading(false);
      });
    return () => {
      d.processNotification.Clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeUser = (e: any) => {
    const selectedUser = d.processNotification.users.find(
      (user: User) => user.UserId === e.target.value
    );
    if (!selectedUser) {
      return;
    }
    d.processNotification.selectedUser = selectedUser;
    p.list.reset();
    p.changed();
  };

  const users = d.processNotification.users
    .sort((a, b) => {
      if (a.AccountType === b.AccountType) {
        if (a.family_name === b.family_name) {
          return a.given_name.localeCompare(b.given_name);
        } else {
          return a.family_name.localeCompare(b.family_name);
        }
      } else {
        return types.indexOf(a.AccountType) > types.indexOf(b.AccountType)
          ? 1
          : -1;
      }
    })
    .map((user: User) => {
      return (
        <MenuItem value={user.UserId} key={user.UserId}>
          <div>{user.family_name + user.given_name}</div>
        </MenuItem>
      );
    });

  return (
    <>
      <div style={{ marginTop: "16px" }} />
      <Select
        size="small"
        labelId="select_user_label"
        id="select_user_id"
        defaultValue=""
        value={
          users.length > 0 ? d.processNotification.selectedUser.UserId : ""
        }
        onChange={handleChangeUser}
        style={{
          width: "300px",
          marginBottom: "16px",
          paddingRight: "35px",
        }}
      >
        {users}
      </Select>
      <DeviceTable />
      <Backdrop open={loading} sx={{ zIndex: 1201 }}>
        <CircularProgress sx={{ color: "white" }} />
      </Backdrop>
    </>
  );
};

const PermissionSettingPage = () => {
  return (
    <PageTemplate title="表示・通知設定">
      <BreadCrumbList
        items={[
          new BreadCrumbItem("TOP", "/top/"),
          new BreadCrumbItem("表示・通知設定"),
        ]}
      />
      <Contents />
    </PageTemplate>
  );
};

export default PermissionSettingPage;
